import { apiClient } from '@/lib/api-client';
import {
    UpdateCompanyProfileRequest,
    UpdateCompanyProfileResponse,
    AddTeamMembersRequest,
    AddTeamMembersResponse,
    GetCompanyDataResponse,
    CompanyConfigurationRequest,
    CompanyConfigurationResponse,
    GetRolesResponse,
    CompanyElementsRequest,
    CompanyElementsResponse,
    OnboardingStatusResponse,
    CompleteOnboardingResponse,
    UploadLogoResponse,
    InviteTeamMemberRequest,
    InviteTeamMemberResponse,
    GetUserDetailsResponse,
    UpdateUserRequest,
    UpdateUserResponse
} from '@/types/api';

export const companyApi = {
    // Get company data with specific tab
    getCompanyData: async (companyId: string, tabName: string): Promise<GetCompanyDataResponse> => {
        return apiClient.get<GetCompanyDataResponse>(`/companies/${companyId}?include=${tabName}`);
    },

    // Update company profile endpoint
    updateProfile: async (companyId: string, data: UpdateCompanyProfileRequest): Promise<UpdateCompanyProfileResponse> => {
        return apiClient.put<UpdateCompanyProfileResponse>(`/companies/${companyId}/profile`, data);
    },

    // Add team members endpoint
    addTeamMembers: async (companyId: string, data: AddTeamMembersRequest): Promise<AddTeamMembersResponse> => {
        return apiClient.post<AddTeamMembersResponse>(`/companies/${companyId}/team-members`, data);
    },

    // Invite team member endpoint
    inviteTeamMember: async (companyId: string, data: InviteTeamMemberRequest): Promise<InviteTeamMemberResponse> => {
        return apiClient.post<InviteTeamMemberResponse>(`/companies/${companyId}/invite-team-member`, data);
    },

    // Resend invitation endpoint
    resendInvitation: async (companyId: string, data: { userId: string }): Promise<{ message: string; success: boolean }> => {
        return apiClient.post<{ message: string; success: boolean }>(`/companies/${companyId}/resend-invitation`, data);
    },

    // Update team member roles endpoint
    updateTeamMemberRoles: async (userId: string, roleIds: string[]): Promise<void> => {
        return apiClient.put<void>(`/companies/team-members/${userId}/roles`, { roleIds });
    },

    // Update company configuration endpoint
    updateConfiguration: async (companyId: string, data: CompanyConfigurationRequest): Promise<CompanyConfigurationResponse> => {
        return apiClient.put<CompanyConfigurationResponse>(`/companies/${companyId}/configuration`, data);
    },

    // Update company elements endpoint
    updateElements: async (companyId: string, data: CompanyElementsRequest): Promise<CompanyElementsResponse> => {
        return apiClient.put<CompanyElementsResponse>(`/companies/${companyId}/elements`, data);
    },

    // Get roles endpoint
    getRoles: async (): Promise<GetRolesResponse> => {
        return apiClient.get<GetRolesResponse>('/roles');
    },

    // Get onboarding status endpoint
    getOnboardingStatus: async (companyId: string): Promise<OnboardingStatusResponse> => {
        return apiClient.get<OnboardingStatusResponse>(`/companies/${companyId}/onboarding-status`);
    },

    // Complete onboarding endpoint
    completeOnboarding: async (companyId: string): Promise<CompleteOnboardingResponse> => {
        return apiClient.post<CompleteOnboardingResponse>(`/companies/${companyId}/complete-onboarding`);
    },

    // Upload company logo endpoint
    uploadLogo: async (companyId: string, file: File): Promise<UploadLogoResponse> => {
        const formData = new FormData();
        formData.append('file', file);

        // Pass FormData directly - apiClient will handle it properly now
        return apiClient.post<UploadLogoResponse>(`/uploads/company/${companyId}/logo`, formData);
    },

    // Get user details endpoint
    getUserDetails: async (userId: string): Promise<GetUserDetailsResponse> => {
        return apiClient.get<GetUserDetailsResponse>(`/companies/users/${userId}`);
    },

    // Update user details endpoint
    updateUser: async (userId: string, data: UpdateUserRequest): Promise<UpdateUserResponse> => {
        return apiClient.patch<UpdateUserResponse>(`/users/${userId}`, data);
    },

    // Assign roles to user endpoint
    assignRoles: async (data: {
        userId: string;
        roleIds: string[];
    }): Promise<{ message: string; success: boolean }> => {
        return apiClient.post<{ message: string; success: boolean }>('/roles/assign', data);
    },

    // Deactivate a team member (soft-delete). If sales rep with clients, pass reassignToUserId.
    removeTeamMember: async (
        companyId: string,
        userId: string,
        reassignToUserId?: string,
    ): Promise<{
        requiresReassignment: boolean;
        message: string;
        deactivatedUser?: {
            id: string;
            fullName: string;
            email: string;
            roles: string[];
        };
        assignedClients?: { id: string; fullName: string }[];
        clientsReassigned?: number;
        reassignedTo?: string | null;
        userId?: string;
        fullName?: string;
    }> => {
        return apiClient.delete<any>(`/companies/${companyId}/team-members/${userId}`, {
            body: reassignToUserId ? JSON.stringify({ reassignToUserId }) : undefined,
        });
    },

    // Get eligible sales reps to receive reassigned clients
    getReassignmentTargets: async (
        companyId: string,
        userId: string,
    ): Promise<{
        id: string;
        fullName: string;
        email: string;
        roles: string[];
        assignedClientCount: number;
    }[]> => {
        return apiClient.get<any>(`/companies/${companyId}/team-members/${userId}/reassignment-targets`);
    },

    // Sprint 42-B BE — Colleagues Split Inspector inline deactivate flow.
    // POST /companies/team-members/:userId/deactivate body { reassignTo?, force? }.
    // Returns 409 with `{ assignedClientCount, assignedClients }` when the
    // target has clients and neither reassignTo nor force is supplied.
    deactivateTeamMemberInline: async (
        userId: string,
        body: { reassignTo?: string; force?: boolean },
    ): Promise<{
        message: string;
        deactivatedUser: {
            id: string;
            fullName: string;
            email: string;
            roles: string[];
        };
        clientsReassigned: number;
        reassignedTo: string | null;
        forced: boolean;
    }> => {
        return apiClient.post<any>(`/companies/team-members/${userId}/deactivate`, body);
    },

    reactivateTeamMemberInline: async (
        userId: string,
    ): Promise<{
        message: string;
        reactivatedUser: {
            id: string;
            fullName: string;
            email: string;
            roles: string[];
        };
    }> => {
        return apiClient.post<any>(`/companies/team-members/${userId}/reactivate`, {});
    },

    // Colleagues → Login tab — admin-initiated password reset.
    // POST /users/:userId/reset-password. Generates a single-use
    // set-password link, revokes the target's sessions, and emails them
    // a secure link. Requires USER.RESET_PASSWORD (Owner always allowed).
    resetTeamMemberPassword: async (
        userId: string,
    ): Promise<{ success: boolean; message: string }> => {
        return apiClient.post<{ success: boolean; message: string }>(
            `/users/${userId}/reset-password`,
            {},
        );
    },

    // Sprint 42-B BE — Colleagues Inspector audit timeline read.
    // GET /companies/team-members/:userId/audit?limit=50&cursor=…
    getTeamMemberAuditLog: async (
        userId: string,
        params: { limit?: number; cursor?: string | null } = {},
    ): Promise<{
        items: Array<{
            id: string;
            userId: string;
            actorId: string | null;
            organizationId: string;
            eventType:
                | 'LOGIN'
                | 'ROLE_GRANTED'
                | 'ROLE_REVOKED'
                | 'INVITATION_SENT'
                | 'INVITATION_RESENT'
                | 'INVITATION_ACCEPTED'
                | 'DEACTIVATED'
                | 'REACTIVATED'
                | 'REASSIGNED';
            metadata: Record<string, any> | null;
            createdAt: string;
            actor: { id: string; fullName: string; email: string } | null;
        }>;
        nextCursor: string | null;
    }> => {
        return apiClient.get<any>(`/companies/team-members/${userId}/audit`, {
            params: {
                ...(params.limit != null ? { limit: params.limit } : {}),
                ...(params.cursor != null ? { cursor: params.cursor } : {}),
            },
        });
    },

    // Get team members with search and pagination
    getTeamMembers: async (params: {
        organizationId: string;
        page?: number;
        limit?: number;
        search?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        roleName?: string;
        verified?: boolean;
        memberStatus?: 'ACTIVE' | 'INACTIVE' | 'PENDING';
    }): Promise<{
        data: {
            message: string;
            totalInvitees: number;
            invitees: any[];
            summary: {
                total: number;
                verified: number;
                unverified: number;
                expiredInvitations: number;
                activeInvitations: number;
            };
        };
        pagination: {
            page: string;
            limit: string;
            total: number;
            totalPages: number;
            hasNext: boolean;
            hasPrev: boolean;
        };
    }> => {
        const queryParams = new URLSearchParams({
            ...(params.page && { page: params.page.toString() }),
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.search && { search: params.search }),
            ...(params.sortBy && { sortBy: params.sortBy }),
            ...(params.sortOrder && { sortOrder: params.sortOrder }),
            ...(params.roleName && { roleName: params.roleName }),
            ...(params.verified !== undefined && { verified: params.verified.toString() }),
            ...(params.memberStatus && { memberStatus: params.memberStatus }),
        });

        return apiClient.get<{
            data: {
                message: string;
                totalInvitees: number;
                invitees: any[];
                summary: {
                    total: number;
                    verified: number;
                    unverified: number;
                    expiredInvitations: number;
                    activeInvitations: number;
                };
            };
            pagination: {
                page: string;
                limit: string;
                total: number;
                totalPages: number;
                hasNext: boolean;
                hasPrev: boolean;
            };
        }>(`/companies/${params.organizationId}/invitee-team-members?${queryParams.toString()}`);
    },

    // Get company owner/administrator
    getCompanyOwner: async (companyId: string): Promise<{ id: string; fullName: string; email: string; phone: string | null; createdAt: string }> => {
        return apiClient.get<{ id: string; fullName: string; email: string; phone: string | null; createdAt: string }>(`/companies/${companyId}/owner`);
    },

    // Update company owner/administrator
    updateCompanyOwner: async (companyId: string, data: { fullName?: string; phone?: string }): Promise<{ message: string; owner: any }> => {
        return apiClient.put<{ message: string; owner: any }>(`/companies/${companyId}/owner`, data);
    },
};
